import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { ComponentPropsWithoutRef, ElementType } from "react";

import { useMenuContext } from "./MenuContext";

export type MenuItemProps<T extends ElementType> = {
  as?: T;
  onSelect?: () => void;
  /**
   * Optional Font Awesome icon rendered to the left of `children`. Accepts any
   * `IconProp` (icon name string, ["fas", "check"] tuple, etc.).
   */
  icon?: IconProp;
  disabled?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "onSelect" | "icon" | "disabled">;

/**
 * A single menu entry. Renders as a <button> by default, or any element/
 * component via `as` (e.g. react-router's Link). Closes the menu whenever
 * it's activated, after calling the passed onSelect/onClick.
 */
function MenuItem<T extends ElementType = "button">({
  as,
  onSelect,
  onClick,
  icon,
  className,
  children,
  disabled = false,
  ...rest
}: MenuItemProps<T>) {
  const { close, open } = useMenuContext();
  const Component = as || "button";

  const handleClick = (e: any) => {
    if (disabled) {
      e.preventDefault?.();
      e.stopPropagation?.();
      return;
    }
    onClick?.(e);
    onSelect?.();
    close();
  };

  const typeProp = Component === "button" ? { type: "button" as const } : {};
  const disabledProp =
    Component === "button"
      ? { disabled }
      : disabled
      ? { "aria-disabled": true }
      : {};

  return (
    <Component
      {...rest}
      {...typeProp}
      {...disabledProp}
      role="menuitem"
      tabIndex={open && !disabled ? (rest as any).tabIndex : -1}
      className={classNames("Menu__item", className, {
        "Menu__item--disabled": disabled,
      })}
      onClick={handleClick}
    >
      {icon && <FontAwesomeIcon icon={icon} className="Menu__item__icon" />}
      {children}
    </Component>
  );
}

export default MenuItem;
